import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiUsers, FiSearch, FiEye, FiAlertCircle, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import './MaintenanceMahasiswa.css';

const MaintenanceMahasiswa = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    filterType: 'all', // all, no-akm, no-khs, no-krs
    enrollmentYear: '',
    program: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({
    total: 0,
    noAKM: 0,
    noKHS: 0,
    noKRS: 0
  });
  const [syncing, setSyncing] = useState({});
  const [programList, setProgramList] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/students/maintenance/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchProgramList = useCallback(async () => {
    try {
      const res = await axios.get('/api/students/programs');
      setProgramList(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch program list:', error);
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const res = await axios.get(`/api/students/maintenance?${queryParams}`);
      setStudents(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      toast.error('Gagal mengambil data mahasiswa');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStudents();
    fetchStats();
    fetchProgramList();
  }, [fetchStudents, fetchStats, fetchProgramList]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
      page: 1
    });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const getFilterTypeLabel = (type) => {
    switch(type) {
      case 'no-akm': return 'Tanpa AKM';
      case 'no-khs': return 'Tanpa KHS';
      case 'no-krs': return 'Tanpa KRS';
      default: return 'Semua';
    }
  };

  const handleSyncStudent = async (studentId, nim) => {
    const key = `${studentId}-sync`;
    setSyncing(prev => ({ ...prev, [key]: true }));
    
    try {
      const res = await axios.post(`/api/students/maintenance/sync-all`, { nim });
      toast.success(res.data.message || 'Sync data berhasil');
      
      // Refresh data
      fetchStudents();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal sync data');
    } finally {
      setSyncing(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDeleteByFilter = () => {
    if (!filters.enrollmentYear && !filters.program) {
      toast.error('Pilih minimal satu filter (Tahun Masuk atau Program Studi)');
      return;
    }
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const params = {};
      if (filters.enrollmentYear) params.enrollmentYear = filters.enrollmentYear;
      if (filters.program) params.program = filters.program;

      const queryParams = new URLSearchParams(params).toString();
      const res = await axios.delete(`/api/students/maintenance/delete-by-filter?${queryParams}`);
      
      toast.success(res.data.message || 'Data mahasiswa berhasil dihapus');
      setDeleteModal(false);
      
      // Reset filters
      setFilters({
        search: '',
        filterType: 'all',
        enrollmentYear: '',
        program: '',
        page: 1,
        limit: 10
      });
      
      // Refresh data
      fetchStudents();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menghapus data mahasiswa');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="maintenance-mahasiswa-page">
      {/* Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiUsers className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Maintenance Mahasiswa</h1>
              <p className="header-subtitle">Monitoring dan pemeliharaan data mahasiswa</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-card-total">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Mahasiswa</p>
            <h3 className="stat-value">{stats.total}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-warning">
          <div className="stat-icon">
            <FiAlertCircle />
          </div>
          <div className="stat-info">
            <p className="stat-label">Tanpa AKM</p>
            <h3 className="stat-value">{stats.noAKM}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-warning">
          <div className="stat-icon">
            <FiAlertCircle />
          </div>
          <div className="stat-info">
            <p className="stat-label">Tanpa KHS</p>
            <h3 className="stat-value">{stats.noKHS}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-warning">
          <div className="stat-icon">
            <FiAlertCircle />
          </div>
          <div className="stat-info">
            <p className="stat-label">Tanpa KRS</p>
            <h3 className="stat-value">{stats.noKRS}</h3>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card filters-card">
        <div className="filters-grid">
          <div className="form-group">
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                name="search"
                placeholder="Cari NIM atau nama mahasiswa..."
                value={filters.search}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-group">
            <select
              name="filterType"
              value={filters.filterType}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="all">Semua Mahasiswa</option>
              <option value="no-akm">Tanpa AKM</option>
              <option value="no-khs">Tanpa KHS</option>
              <option value="no-krs">Tanpa KRS</option>
            </select>
          </div>

          <div className="form-group">
            <select
              name="enrollmentYear"
              value={filters.enrollmentYear}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">Semua Tahun Masuk</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
              <option value="2022">2022</option>
              <option value="2021">2021</option>
              <option value="2020">2020</option>
            </select>
          </div>

          <div className="form-group">
            <select
              name="program"
              value={filters.program}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">Semua Program Studi</option>
              {programList.map((program, index) => (
                <option key={index} value={program}>
                  {program}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <button
              onClick={handleDeleteByFilter}
              className="btn btn-danger btn-delete-filter"
              disabled={!filters.enrollmentYear && !filters.program}
              title="Hapus data berdasarkan filter"
            >
              <FiTrash2 />
              <span>Hapus Data</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="spinner"></div>
        ) : students.length === 0 ? (
          <div className="empty-state">
            <FiUsers size={48} />
            <p>Tidak ada data mahasiswa yang ditemukan</p>
          </div>
        ) : (
          <>
            <div className="table-header-info">
              <p>
                Menampilkan <strong>{getFilterTypeLabel(filters.filterType)}</strong>: 
                <strong> {pagination.total}</strong> mahasiswa
              </p>
            </div>
            <div className="modern-table-container">
              <table className="modern-table accent-orange">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>NIM</th>
                    <th>Nama</th>
                    <th>Program Studi</th>
                    <th>Tahun Masuk</th>
                    <th>Status AKM</th>
                    <th>Status KHS</th>
                    <th>Status KRS</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, idx) => (
                    <tr key={student._id}>
                      <td>{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                      <td>{student.nim}</td>
                      <td><strong>{student.name}</strong></td>
                      <td>{student.nama_program_studi || student.program || '-'}</td>
                      <td>{student.enrollmentYear || '-'}</td>
                      <td>
                        {student.hasAKM ? (
                          <span className="table-badge table-badge-success">Ada</span>
                        ) : (
                          <span className="table-badge table-badge-danger">Tidak Ada</span>
                        )}
                      </td>
                      <td>
                        {student.hasKHS ? (
                          <span className="table-badge table-badge-success">Ada</span>
                        ) : (
                          <span className="table-badge table-badge-danger">Tidak Ada</span>
                        )}
                      </td>
                      <td>
                        {student.hasKRS ? (
                          <span className="table-badge table-badge-success">Ada</span>
                        ) : (
                          <span className="table-badge table-badge-danger">Tidak Ada</span>
                        )}
                      </td>
                      <td>
                        <div className="table-actions sync-actions">
                          {(!student.hasAKM || !student.hasKHS || !student.hasKRS) && (
                            <button
                              onClick={() => handleSyncStudent(student._id, student.nim)}
                              className="table-btn table-btn-sync-all"
                              disabled={syncing[`${student._id}-sync`]}
                              title="Sync Data"
                            >
                              <FiRefreshCw className={syncing[`${student._id}-sync`] ? 'spinning' : ''} />
                              <span>Sync Data</span>
                            </button>
                          )}
                          <Link
                            to={`/students/edit/${student._id}`}
                            className="table-btn table-btn-view"
                            title="Detail"
                          >
                            <FiEye />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="table-pagination">
                <div className="table-pagination-info">
                  Halaman {pagination.page} dari {pagination.pages} ({pagination.total} data)
                </div>
                <div className="table-pagination-buttons">
                  <button
                    className="table-pagination-button"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <button
                    className="table-pagination-button"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Konfirmasi Hapus Data</h3>
            </div>
            <div className="modal-body">
              <p>
                Apakah Anda yakin ingin menghapus data mahasiswa dengan kriteria:
              </p>
              <ul className="delete-criteria">
                {filters.enrollmentYear && (
                  <li><strong>Tahun Masuk:</strong> {filters.enrollmentYear}</li>
                )}
                {filters.program && (
                  <li><strong>Program Studi:</strong> {filters.program}</li>
                )}
              </ul>
              <p className="warning-text">
                <FiAlertCircle /> Tindakan ini tidak dapat dibatalkan!
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setDeleteModal(false)}
                className="btn btn-secondary"
                disabled={deleting}
              >
                Batal
              </button>
              <button
                onClick={confirmDelete}
                className="btn btn-danger"
                disabled={deleting}
              >
                {deleting ? 'Menghapus...' : 'Hapus Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceMahasiswa;
